import { Toggle } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import 'react-datepicker/dist/react-datepicker.css'
import { FormattedMessage, injectIntl, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import { createHubTask, pollTaskStatus } from '../../../../../crud/integrationHub.crud'
import Spinner from '../../../../../partials/content/Spinner'
import { getBrowserTimeZone } from '../../../../../util/date.util'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../../../util/table.util'

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onUpdate: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.array,
}

function setDate(isoString, newDate) {
  const timePart = isoString?.split('T')[1] || '00:00:00.000Z'
  return `${newDate}T${timePart}`
}

function setTime(isoString, newTime) {
  const datePart = isoString.split('T')[0] || '1970-01-01'
  return new Date(`${datePart}T${newTime}:00`).toISOString()
}

function setDatePlusOneDay(isoString, newDate) {
  const timePart = isoString?.split('T')[1] || '00:00:00.000Z'

  const date = new Date(newDate)
  date.setDate(date.getDate() + 1)

  const updatedDate = date.toISOString().split('T')[0]
  return `${updatedDate}T${timePart}`
}

const getDefaultDateValue = (addDays = 0) => {
  const date = new Date()
  date.setDate(date.getDate() + addDays)

  return date.toISOString()
}

const getDefaultTimeValue = () => {
  const date = new Date()

  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function OutOfOfficeModal(props) {
  const { show = false, onHide = () => {}, onUpdate = () => {}, employees = [] } = props ?? {}
  const intl = useIntl()

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  const STATUS = {
    REVIEW: 'REVIEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  }

  const ERRORS = {
    GLOBAL_ERRORS: 'GLOBAL_ERRORS',
    OPERATIONAL: 'OPERATIONAL',
  }

  const [taskErrors, setTaskErrors] = useState({})
  const [status, setStatus] = useState(STATUS.REVIEW)
  const [isOutOfOfficeOn, setIsOutOfOfficeOn] = useState(false)
  const [isSendDuringPeriod, setIsSendDuringPeriod] = useState(false)

  const [automaticRepliesSetting, setAutomaticRepliesSetting] = useState({
    status: 'Disabled',
    scheduledStartDateTime: {
      dateTime: getDefaultDateValue(),
      timeZone: getBrowserTimeZone() || 'UTC',
    },
    scheduledEndDateTime: {
      dateTime: getDefaultDateValue(1),
      timeZone: getBrowserTimeZone() || 'UTC',
    },
    internalReplyMessage: '',
    externalReplyMessage: '',
  })

  const onApply = async () => {
    setStatus(STATUS.IN_PROGRESS)

    const parameters = {
      actions: {
        employeeIdentifiers: employees?.map((emp) => emp.employeeIdentifier || emp.employee_identifier),
        automaticRepliesSetting,
      },
      tasktype: TASK_TYPES.outOfOffice,
      action: TASK_ACTIONS.update,
      customerId: selectedCustomer?.id,
      userId: currentUser?.id,
      organizationId: selectedCustomer?.organization,
    }

    try {
      const { data: { taskId } = {} } = await createHubTask(parameters)

      if (taskId) {
        const { data: { _source: task } = {} } =
          (await pollTaskStatus(taskId, {
            retries: 10,
            timeout: 3000,
          })) ?? {}

        setTaskErrors(JSON.parse(task?.errors || '{}'))
        setStatus(STATUS.COMPLETED)

        await onUpdate()
      }
    } catch (error) {
      setTaskErrors({
        OPERATIONAL: { message: error.message },
      })
      setStatus(STATUS.COMPLETED)
    }

    setTimeout(() => {
      setStatus(STATUS.COMPLETED)
    }, 2000)
  }

  const onToggle = (value) => {
    setAutomaticRepliesSetting({
      ...automaticRepliesSetting,
      status: (() => {
        if (value) {
          return isSendDuringPeriod ? 'Scheduled' : 'Enabled'
        }
        return 'Disabled'
      })(),
    })
    setIsOutOfOfficeOn(value)
  }

  const onCheckSendDuringPeriod = (checked) => {
    setAutomaticRepliesSetting({
      ...automaticRepliesSetting,
      status: checked ? 'Scheduled' : 'Enabled',
    })
    setIsSendDuringPeriod(checked)
  }

  const onStartDateChange = (value) => {
    let update = {
      ...automaticRepliesSetting,
      scheduledStartDateTime: {
        ...automaticRepliesSetting.scheduledStartDateTime,
        dateTime: setDate(automaticRepliesSetting.scheduledStartDateTime.dateTime, value),
      },
    }

    if (new Date(automaticRepliesSetting.scheduledEndDateTime.dateTime?.split('T')[0]) <= new Date(value)) {
      update = {
        ...update,
        scheduledEndDateTime: {
          ...automaticRepliesSetting.scheduledEndDateTime,
          dateTime: setDatePlusOneDay(automaticRepliesSetting.scheduledEndDateTime.dateTime, value),
        },
      }
    }

    setAutomaticRepliesSetting(update)
  }

  const onStartTimeChange = (value) => {
    setAutomaticRepliesSetting({
      ...automaticRepliesSetting,
      scheduledStartDateTime: {
        ...automaticRepliesSetting.scheduledStartDateTime,
        dateTime: setTime(automaticRepliesSetting.scheduledStartDateTime.dateTime, value),
      },
    })
  }

  const onEndDateChange = (value) => {
    setAutomaticRepliesSetting({
      ...automaticRepliesSetting,
      scheduledEndDateTime: {
        ...automaticRepliesSetting.scheduledEndDateTime,
        dateTime: setDate(automaticRepliesSetting.scheduledEndDateTime.dateTime, value),
      },
    })
  }

  const onEndTimeChange = (value) => {
    setAutomaticRepliesSetting({
      ...automaticRepliesSetting,
      scheduledEndDateTime: {
        ...automaticRepliesSetting.scheduledEndDateTime,
        dateTime: setTime(automaticRepliesSetting.scheduledEndDateTime.dateTime, value),
      },
    })
  }

  return (
    <Modal show={show} size="lg" className={`right ${clsx({ in: show })}`} backdrop onHide={onHide}>
      <Modal.Header closeButton className="grey-bg-color-1">
        <Modal.Title id="contained-modal-title-vcenter">
          <FormattedMessage id="ENGAGE.OUT_OF_OFFICE" />
        </Modal.Title>

        {employees?.length === 1 && (
          <p className="mt-2">
            {employees?.[0]?.firstName} {employees?.[0]?.lastName}
          </p>
        )}

        <p className="mt-2">
          {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
            ? 'Microsoft 365'
            : selectedIntegration?.IntegrationApplication?.display_name}
        </p>
      </Modal.Header>
      <Modal.Body className={clsx('modal-body-sidebar', 'out-of-office-modal')}>
        {[STATUS.REVIEW].includes(status) && (
          <>
            <Row className="px-2">
              <Col>
                <p>
                  <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_DESC" />
                </p>
              </Col>
            </Row>

            <Row className="px-2 mt-4 mb-2">
              <Col>
                <Toggle
                  id="out-of-office-automatic-replies"
                  className="out-of-office-modal__automatic-replies-toggle"
                  checked={isOutOfOfficeOn}
                  onChange={onToggle}
                  labelText={intl.formatMessage({
                    id: isOutOfOfficeOn ? 'ENGAGE.OUT_OF_OFFICE_ON' : 'ENGAGE.OUT_OF_OFFICE_OFF',
                  })}
                  labelTextStyle="out-of-office-modal__automatic-replies-toggle-label"
                  ariaLabel={intl.formatMessage({
                    id: isOutOfOfficeOn ? 'ENGAGE.OUT_OF_OFFICE_ON' : 'ENGAGE.OUT_OF_OFFICE_OFF',
                  })}
                />
              </Col>
            </Row>

            <Row className="px-2 mb-2">
              <Col>
                <div className="text-color-default">
                  <Form.Check
                    className="out-of-office-modal__period-check mb-3"
                    type="checkbox"
                    id="reply-period-checkbox"
                    disabled={!isOutOfOfficeOn}
                    checked={!!isSendDuringPeriod}
                    onChange={(e) => onCheckSendDuringPeriod(e.target.checked)}
                    label={<FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_DURING_PERIOD" />}
                  />
                </div>
              </Col>
            </Row>

            <Row className="px-4 mb-4">
              <Col>
                <div className="text-color-default">
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_START_TIME" />
                    </Form.Label>
                    <Row>
                      <Col md="7" className="pe-0">
                        <Form.Control
                          id="start-date"
                          type="date"
                          name="start-date"
                          disabled={!isOutOfOfficeOn || !isSendDuringPeriod}
                          value={automaticRepliesSetting?.scheduledStartDateTime?.dateTime?.split('T')?.[0]}
                          onKeyDown={(e) => e.preventDefault()}
                          min={getDefaultDateValue()?.split('T')?.[0]}
                          onChange={(e) => onStartDateChange(e.target.value)}
                        />
                      </Col>
                      <Col md="5" className="ps-1">
                        <Form.Control
                          id="start-time"
                          type="time"
                          name="start-time"
                          disabled={!isOutOfOfficeOn || !isSendDuringPeriod}
                          defaultValue={getDefaultTimeValue()}
                          onChange={(e) => onStartTimeChange(e.target.value)}
                        />
                      </Col>
                    </Row>
                    <div className="text-color-secondary font-size-sm">
                      <FormattedMessage id="GENERAL.TIME_ZONE" />
                      :&nbsp;{getBrowserTimeZone()}
                    </div>
                  </Form.Group>
                </div>
              </Col>
              <Col>
                <div className="text-color-default">
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_END_TIME" />
                    </Form.Label>
                    <Row>
                      <Col md="7" className="pe-0">
                        <Form.Control
                          type="date"
                          name="end-date"
                          disabled={!isOutOfOfficeOn || !isSendDuringPeriod}
                          value={automaticRepliesSetting?.scheduledEndDateTime?.dateTime?.split('T')?.[0]}
                          min={
                            new Date(
                              new Date(automaticRepliesSetting?.scheduledStartDateTime?.dateTime).setDate(
                                new Date(automaticRepliesSetting?.scheduledStartDateTime?.dateTime).getDate() + 1,
                              ),
                            )
                              ?.toISOString()
                              ?.split('T')?.[0]
                          }
                          onKeyDown={(e) => e.preventDefault()}
                          onChange={(e) => onEndDateChange(e.target.value)}
                        />
                      </Col>
                      <Col md="5" className="ps-1">
                        <Form.Control
                          type="time"
                          name="end-time"
                          disabled={!isOutOfOfficeOn || !isSendDuringPeriod}
                          defaultValue={getDefaultTimeValue()}
                          onChange={(e) => onEndTimeChange(e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </div>
              </Col>
            </Row>

            <Row className="px-3 mb-2">
              <Col>
                <div className="text-color-default">
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_ORGANIZATION" />
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      disabled={!isOutOfOfficeOn}
                      placeholder={intl.messages['ENGAGE.OUT_OF_OFFICE_SEND_ADD_MESSAGE']}
                      name="organization-message"
                      id="organization-message"
                      onChange={(e) => {
                        setAutomaticRepliesSetting({
                          ...automaticRepliesSetting,
                          internalReplyMessage: e.target.value,
                        })
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>

            <Row className="px-3 mb-2">
              <Col>
                <div className="text-color-default">
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_EXTERNAL" />
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      disabled={!isOutOfOfficeOn}
                      placeholder={intl.messages['ENGAGE.OUT_OF_OFFICE_SEND_ADD_MESSAGE']}
                      name="external-message"
                      id="external-message"
                      onChange={(e) => {
                        setAutomaticRepliesSetting({
                          ...automaticRepliesSetting,
                          externalReplyMessage: e.target.value,
                        })
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md="12">
                <p className="mb-1 mt-4 fw-semibold">
                  <FormattedMessage
                    id="ENGAGE.OUT_OF_OFFICE_APPLY_PHRASE"
                    values={{ s: employees?.length > 1 ? 's' : '' }}
                  />
                </p>

                <ul className="mb-0 ps-3">
                  {employees?.map((employee) => (
                    <li key={employee.id}>{getEmployeeFullName(employee)}</li>
                  ))}
                </ul>
              </Col>
            </Row>
          </>
        )}

        {[STATUS.IN_PROGRESS].includes(status) && (
          <div className="modal-loading-centered">
            <Spinner size="small" />
            <div className="mt-4 text-strong text-color-secondary">
              <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_IN_PROGRESS" />
            </div>
          </div>
        )}

        {!taskErrors[ERRORS.GLOBAL_ERRORS] &&
          !taskErrors[ERRORS.OPERATIONAL] &&
          [STATUS.COMPLETED].includes(status) && (
            <ul className="ps-0">
              {employees?.map((employee) => {
                const employeeId = employee?.employeeIdentifier || employee?.employee_identifier
                const hasError = !!taskErrors[employeeId]

                return (
                  <li key={employee.id || employeeId} className="list-unstyled mt-0">
                    {hasError ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
                      </div>
                    ) : (
                      <i className="icon-xl align-text-bottom font-color-engage fas fa-circle-check me-2" />
                    )}
                    {getEmployeeFullName(employee)}

                    {hasError && (
                      <div style={{ width: '100%' }} className="font-color-danger">
                        <pre
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
                          className="font-color-danger"
                        >
                          {JSON.stringify(taskErrors[employeeId] || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

        {taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
          </div>
        )}

        {taskErrors[ERRORS.GLOBAL_ERRORS] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
            <pre
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
              className="font-color-danger mt-1"
            >
              {JSON.stringify(taskErrors[ERRORS.GLOBAL_ERRORS] || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={clsx('d-flex', 'justify-content-between')}>
        <Button onClick={onHide} variant="secondary">
          <FormattedMessage id={[STATUS.REVIEW].includes(status) ? 'GENERAL.CANCEL' : 'GENERAL.CLOSE'} />
        </Button>
        {[STATUS.REVIEW].includes(status) && (
          <Button
            id="out-of-office-submit-btn"
            variant="primary"
            onClick={onApply}
            className="bg-color-engage pull-right"
            disabled={!isOutOfOfficeOn}
          >
            <FormattedMessage id="GENERAL.APPLY" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

OutOfOfficeModal.propTypes = propTypes

export default injectIntl(OutOfOfficeModal)
